// Subsequent-filing roster hydration (SOR-220 — canonical attorney linkage +
// represent-aware self-rep flag).
//
// When an existing JTI case is attached (OrderInfoTab.handleCaseSelected), the
// GetCase parties and attorneys are minted into fresh wizard-local records. Two
// representation invariants must hold off the canonical (deduplicated) roster:
//
//  - REPRESENTEDBY linkage (R4): each attorney's `representedPartyIds` must
//    reference the canonical party's local id. GetCase returns one party record
//    per subcase, so a logical party recurs across subcases — its occurrences
//    share a stable `referenceId` but carry DISTINCT per-subcase primaryIds.
//    `dedupePartyRosterGrouped` collapses those to one canonical wizard-local
//    party per logical party (referenceId-first, SOR-219/SOR-278) and returns the
//    occurrence→canonical resolution, so `partyLocalIdByCourtId` maps EVERY merged
//    primaryId — including a non-surviving occurrence's — to that one canonical
//    local id. The attorney's `partyId` is a primaryId (possibly a dropped
//    occurrence's), so it still resolves straight to the canonical party.
//
//  - Self-rep flag (R5): a party has an attorney of record exactly when some
//    wizard-local attorney is linked to its canonical local id, and is
//    self-represented exactly when it holds a filing-party role AND has no such
//    linked attorney. Because the flag is derived from `representedPartyIds`, it
//    is computed only AFTER attorneys are minted — a represented party (its
//    canonical id present in some attorney's `representedPartyIds`) is never
//    marked self-represented.
//
// Pure and side-effect-free apart from the injected id generator (default
// `crypto.randomUUID`), so R4/R5 are unit-testable with a deterministic id.

import type { EFilingParty, EFilingAttorney } from '@lds-efsp/shared-contracts';
import { isRoleSelfRepEligible } from '@lds-efsp/shared-contracts';
import { dedupePartyRosterGrouped } from './dedupe-party-roster';

/** A GetCase party occurrence as surfaced to OrderInfoTab.handleCaseSelected. */
export interface CaseRosterParty {
  id: string;
  /** Stable cross-subcase logical-party id — the dedup key (referenceId-first). */
  referenceId?: string;
  firstName?: string;
  lastName?: string;
  organizationName?: string;
  role: string;
  isOrganization: boolean;
  /**
   * Court-record mailing address (WOL-448 / Walter R97 — parsed from the
   * GetCase ContactInformation block). Carried onto the minted party's
   * `mailingAddress`, and copied into `selfRepAddress` when the party derives
   * self-represented so the self-rep contact gate pre-fills instead of forcing
   * re-entry on every subsequent filing.
   */
  mailingAddress?: {
    address1?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  /** Court-record email — Madera returns none (probe 2026-07-14); used when present. */
  email?: string;
  /**
   * Court-REPORTED fee-waiver fields (SOR-239, parsed by the case-lookup
   * response off CaseParticipantExt). Carried onto the minted party's
   * read-only `courtWaiver` — never merged into the filer's own
   * `feeWaiverOnFile` flag, which hydration keeps hardcoded false.
   */
  courtWaiver?: {
    efspFeeWaiverFiled?: boolean;
    efmExemptionType?: string;
    efmFeeExemptionType?: string;
  };
}

/** A GetCase attorney of record as surfaced to OrderInfoTab.handleCaseSelected. */
export interface CaseRosterAttorney {
  id: string;
  barNumber?: string;
  firstName?: string;
  lastName?: string;
  firmName?: string;
  partyId?: string;
}

export interface HydratedRoster {
  parties: EFilingParty[];
  attorneys: EFilingAttorney[];
}

/**
 * Mint canonical wizard-local parties + attorneys from a GetCase roster, linking
 * each attorney to the canonical party of any occurrence it represents — including
 * a non-surviving one (R4) — and deriving each party's `isSelfRepresented` from
 * that linkage (R5).
 *
 * @param newId injectable id generator (default `crypto.randomUUID`) — lets tests
 *   assert exact attorney→party linkage deterministically.
 */
export function hydrateCaseRoster(
  parties: CaseRosterParty[],
  attorneys: CaseRosterAttorney[],
  newId: () => string = () => crypto.randomUUID(),
): HydratedRoster {
  // Court party id → canonical wizard-local UUID, so attorney representedPartyIds
  // can reference the local party records (the validator and envelope builder
  // match attorneys to parties by local party.id, not externalId).
  const partyLocalIdByCourtId = new Map<string, string>();

  // GetCase returns one party record PER SUBCASE, so a logical party recurs
  // across subcases — its occurrences share a stable `referenceId` but carry
  // DISTINCT per-subcase primaryIds. Collapse to one canonical wizard-local party
  // per distinct logical party (referenceId-first, then normalized name + role)
  // BEFORE minting (SOR-219/SOR-278, SPEC-SOR-277-v1 R1/R2). Build the dedup input
  // array ONCE: `dedupePartyRosterGrouped` keys its occurrence→canonical
  // `resolution` by object identity, so the SAME instances must be reused when the
  // map is built below — re-mapping `parties` a second time would produce fresh
  // objects absent from `resolution`. Surface the primaryId as `externalId`
  // (empty → undefined); the `...p` spread carries `referenceId` (the dedup key).
  // Index-0 lead-client survives because dedup keeps the first occurrence.
  const dedupInput = parties.map(p => ({ ...p, externalId: p.id || undefined }));
  const { canonical, resolution } = dedupePartyRosterGrouped(dedupInput);

  // Mint one canonical party per logical party, in first-occurrence order, and
  // remember each canonical occurrence → its minted local id (set in the same
  // callback so two distinct canonical occurrences never alias to one local id).
  const localIdByCanonical = new Map<(typeof dedupInput)[number], string>();
  const mappedParties = canonical.map((p, i): EFilingParty => {
    const id = newId();
    localIdByCanonical.set(p, id);
    return {
      id,
      externalId: p.id || undefined,
      partyType: p.isOrganization ? 'organization' as const : 'person' as const,
      roleCode: p.role,
      roleName: p.role,
      firstName: p.firstName || undefined,
      lastName: p.lastName || undefined,
      organizationName: p.organizationName || undefined,
      // Court-record contact (WOL-448 / R97) — canonical = first occurrence,
      // so its contact block wins (same rule as every other field).
      mailingAddress: p.mailingAddress
        ? {
            country: p.mailingAddress.country || 'US',
            address1: p.mailingAddress.address1,
            city: p.mailingAddress.city,
            state: p.mailingAddress.state,
            zip: p.mailingAddress.zip,
          }
        : undefined,
      // Court-reported waiver (SOR-239) — canonical = first occurrence, the
      // same rule as every other carried field. Conditional spread so a
      // court-silent party is minted with NO key at all: absence means the
      // court said nothing, never "no waiver". Read-only display input — the
      // filer's `feeWaiverOnFile` above stays a hardcoded false.
      ...(p.courtWaiver ? { courtWaiver: p.courtWaiver } : {}),
      isLeadClient: i === 0,
      // Finalized after attorney minting, from the canonical linkage (R5).
      isSelfRepresented: false,
      feeExemptionType: 'NONE' as const,
      requestFeesExemption: false,
      feeWaiverOnFile: false,
      eServiceConsent: false,
      hasAlternateNames: false,
      alternateNames: [] as Array<{ type: 'AKA' | 'Alias' | 'DBA' | 'ESA' | 'FDBA' | 'FKA'; name: string }>,
      requiresInterpreter: false,
      partySubTypes: [] as Array<'guardian-ad-litem' | 'incompetent-person' | 'minor'>,
      // Hydrated from existing case → already on-record, not a "new" addition for
      // amended-complaint NEW_PLAINTIFF / NEW_RESPONDING_PARTY emission.
      isNewToCase: false,
    };
  });

  // Map EVERY pre-dedup occurrence's primaryId — survivor AND non-surviving — to
  // its canonical party's local id, via the resolution. An attorney REPRESENTEDBY
  // association references a primaryId that may belong to a dropped occurrence, so
  // it must still resolve to the canonical party (R4). Building only from survivors
  // (the old `externalId`-per-survivor loop) orphaned such an attorney and
  // re-tripped the self-rep misfire.
  for (const occ of dedupInput) {
    const canon = resolution.get(occ);
    const localId = canon && localIdByCanonical.get(canon);
    if (occ.externalId && localId) partyLocalIdByCourtId.set(occ.externalId, localId);
  }

  // representedPartyIds must hold LOCAL party UUIDs — storing the primaryId
  // verbatim made the validator reject with 'references unknown party ID' (Walter
  // cert Sub-1/Sub-2). With the all-occurrences map above, the attorney's primaryId
  // — even a non-surviving occurrence's — resolves to the canonical party's local
  // id (R4).
  const mappedAttorneys = attorneys.map((a): EFilingAttorney => {
    const localPartyId = a.partyId ? partyLocalIdByCourtId.get(a.partyId) : undefined;
    return {
      id: newId(),
      externalId: a.id || undefined,
      barState: 'CA',
      barNumber: a.barNumber || '',
      firstName: a.firstName || '',
      lastName: a.lastName || '',
      firmName: a.firmName || '',
      representedPartyIds: localPartyId ? [localPartyId] : [],
    };
  });

  // R4/R5: a party has an attorney of record exactly when some wizard-local
  // attorney links to its canonical local id; mark it self-represented only when
  // its role is self-rep-eligible AND it has no such linked attorney. A represented
  // party (its id present below) is therefore never marked self-represented.
  //
  // Eligibility is the canonical, side-agnostic `isRoleSelfRepEligible` gate
  // (RoleClass self-rep-visible) — NOT the plaintiff-class subset. A respondent
  // (RES, selfRepVisible: true) self-filing a Response/RFO is a self-rep filing
  // party; the old plaintiff-only gate left RES `isSelfRepresented: false`, so the
  // envelope never took its self-rep (FILING_PARTY_ADDRESS) arm and the Madera
  // clerk flagged the Filed By party as not self-represented (Sub #1, 2026-06-29).
  const linkedLocalIds = new Set(mappedAttorneys.flatMap(a => a.representedPartyIds));
  const emailByLocalId = new Map<string, string>();
  for (const [canon, localId] of localIdByCanonical) {
    if (canon.email) emailByLocalId.set(localId, canon.email);
  }
  for (const party of mappedParties) {
    party.isSelfRepresented =
      isRoleSelfRepEligible(party.roleCode) && !linkedLocalIds.has(party.id);
    // WOL-448 / R97: a self-rep party's court-record contact pre-fills the
    // self-rep gate fields — the filer must not re-type an address the case
    // already holds. Email only when the court returned one (Madera doesn't).
    if (party.isSelfRepresented) {
      if (party.mailingAddress && !party.selfRepAddress) {
        party.selfRepAddress = { ...party.mailingAddress };
      }
      const email = emailByLocalId.get(party.id);
      if (email && !party.selfRepEmail) party.selfRepEmail = email;
    }
  }

  return { parties: mappedParties, attorneys: mappedAttorneys };
}
